import type { KeyboardEvent, ReactNode } from 'react';
import { cn } from '../../lib/cn';
import { Icon, type LucideIcon } from './icon';

export interface CardProps {
  title?: ReactNode;
  description?: ReactNode;
  /** Icon im Kopf, in highlight. */
  icon?: LucideIcon;
  /** Buttons rechts im Kopf. */
  actions?: ReactNode;
  children?: ReactNode;
  /** Ohne Innenabstand, für Tabellen und Listen bis zum Rand. */
  flush?: boolean;
  /** Die ganze Karte als Klickziel (Projekt öffnen): Hover, Fokusring, Enter und Leertaste. Dann keine `actions`. */
  onClick?: () => void;
  className?: string;
}

/** Fläche mit Haarlinie, ohne Schatten. Karten in Karten nur als StatTile. */
export function Card({ title, description, icon, actions, children, flush, onClick, className }: CardProps) {
  const kopf = title || actions;
  const klickbar = onClick
    ? {
        role: 'button',
        tabIndex: 0,
        onClick,
        onKeyDown: (e: KeyboardEvent) => {
          if (e.target !== e.currentTarget || (e.key !== 'Enter' && e.key !== ' ')) return;
          e.preventDefault();
          onClick();
        },
      }
    : {};
  return (
    <section
      {...klickbar}
      className={cn(
        'rounded-lg border border-border bg-card text-card-foreground',
        onClick && 'cursor-pointer transition-colors duration-150 hover:bg-accent',
        className,
      )}
    >
      {kopf && (
        <div className={cn('flex items-start gap-3 px-5 pt-4', flush && 'border-b border-border pb-4')}>
          {icon && <Icon icon={icon} size={18} className="mt-0.5 text-highlight" />}
          <div className="min-w-0 flex-1">
            {title && <h3 className="t-title">{title}</h3>}
            {description && <p className="mt-0.5 t-small text-muted-foreground">{description}</p>}
          </div>
          {actions && <div className="flex items-center gap-2">{actions}</div>}
        </div>
      )}
      {children != null && <div className={cn(flush ? 'p-0' : kopf ? 'px-5 pb-5 pt-4' : 'p-5')}>{children}</div>}
    </section>
  );
}
